'use client'

import React, { ReactNode } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { pathToRegexp } from 'path-to-regexp'
import cn from 'clsx'

type NavLinkProps = {
    href: string
    target?: string
    as?: string
    exact?: boolean
    children?: ReactNode
    innerHTML?: { __html: string }
    className?: string
    onClick?: () => void
    label?: string
    prefetch?: boolean
    forceActive?: boolean
}

export default function NavLink({
    href,
    target,
    children,
    innerHTML,
    className: _className = '',
    onClick,
    label,
    prefetch,
    forceActive,
    ...props
}: NavLinkProps) {
    const asPath = usePathname()

    if (!href) {
        console.warn(`NavLink component is missing a href prop.`)
        return null
    }

    let className = _className

    if (href && href.indexOf('http') !== -1) {
        return (
            <a
                className={className}
                href={href}
                target={target || '_blank'}
                rel="noreferrer"
                dangerouslySetInnerHTML={innerHTML ? { __html: innerHTML.__html } : undefined}
                aria-label={label}
            >
                {children}
            </a>
        )
    }

    if ((href && href.indexOf('tel:') !== -1) || (href && href.indexOf('mailto:') !== -1)) {
        return (
            <a
                className={className}
                href={href}
                target={target || ''}
                dangerouslySetInnerHTML={innerHTML ? { __html: innerHTML.__html } : undefined}
                aria-label={label}
            >
                {children}
            </a>
        )
    }

    const isActive =
        forceActive ||
        pathToRegexp(href.split('?')[0] || '/', [], {
            sensitive: true,
            end: false,
        }).test(asPath)

    const _children = (
        <span dangerouslySetInnerHTML={innerHTML} aria-label={label}>
            {children}
        </span>
    )

    className = cn(className, isActive && 'active')

    return (
        <Link
            href={href}
            target={target || ''}
            {...props}
            aria-label={label}
            prefetch={prefetch}
            onClick={onClick}
            className={className}
        >
            {_children}
        </Link>
    )
}
